/**
 * Data visualization color tokens.
 * Previously hardcoded in tokens.ts — now theme-aware via CSS custom properties.
 *
 * Light and dark theme values are provided so ThemeVars can inject them
 * the same way it handles palette / semantic colors.
 */

// ─── Semantic interface ──────────────────────────────────────────────────────

/** Full set of data-visualization color tokens. */
export interface DataVizColorTokens {
  /** Neutral fill for diamonds, table borders, etc. */
  neutral: string;
  /** Medium gray used for table strokes, borders, and diamond fills */
  strokeGray: string;
  /** Light gray for secondary table borders */
  strokeLightGray: string;
  /** Very light gray for gridlines */
  gridline: string;
  /** Scrollbar / overlay background */
  overlay: string;
  /** Scrollbar track */
  overlayLight: string;
  /** Arrow color for positive export value change */
  arrowPositive: string;
  /** Arrow color for negative export value change */
  arrowNegative: string;
  /** Muted gray for unselected / inactive sort arrows */
  sortArrowUnselected: string;
  /** Unfocused/unselected sort indicator */
  unfocused: string;
  /** Dark text color used across chart axes, labels, table text */
  textDark: string;
  /** Secondary text color for product names and descriptions */
  textSecondary: string;
  /** Dark color for axis tick labels */
  axisText: string;
  /** Warm orange arrow indicating positive trade growth */
  growthPositive: string;
  /** Cool teal arrow indicating negative trade growth */
  growthNegative: string;
  /** Table row hover background */
  tableRowHover: string;
  /** Tooltip header background */
  tooltipHeader: string;
  /** Light border color for subtle separators between elements */
  lightBorder: string;
  /** Dark blue-gray background */
  backgroundDark: string;
  /** Medium gray background */
  backgroundMedium: string;
  /** Loading spinner default background */
  loadingBackground: string;
  /** Primary action color used across viz and UI */
  primaryAction: string;
  /** Primary action hover state */
  primaryActionHover: string;
  /** Glossary/annotation accent color */
  accentBlue: string;
}

// ─── Light theme values ──────────────────────────────────────────────────────

export const dataVizLight: DataVizColorTokens = {
  neutral: "#808080",
  strokeGray: "#808080",
  strokeLightGray: "#b3b3b3",
  gridline: "#e6e6e6",
  overlay: "rgba(0, 0, 0, 0.3)",
  overlayLight: "rgba(0, 0, 0, 0.1)",
  arrowPositive: "#f1c086",
  arrowNegative: "#8fd8e5",
  sortArrowUnselected: "#d6d6d6",
  unfocused: "#d6d6d6",
  textDark: "#333333",
  textSecondary: "#666666",
  axisText: "#232323",
  growthPositive: "#f1c086",
  growthNegative: "#8fd8e5",
  tableRowHover: "#efefef",
  tooltipHeader: "#d7dbdd",
  lightBorder: "#dcdcdc",
  backgroundDark: "#2e353f",
  backgroundMedium: "#e6e7e8",
  loadingBackground: "#7f8c8d",
  primaryAction: "#334c60",
  primaryActionHover: "#2a3f50",
  accentBlue: "#5065a8",
};

// ─── Dark theme values ───────────────────────────────────────────────────────

export const dataVizDark: DataVizColorTokens = {
  neutral: "#909090",
  strokeGray: "#909090",
  strokeLightGray: "#6a6a6a",
  gridline: "#3a3f4a",
  overlay: "rgba(0, 0, 0, 0.5)",
  overlayLight: "rgba(0, 0, 0, 0.25)",
  arrowPositive: "#e8a85c",
  arrowNegative: "#6bc5d4",
  sortArrowUnselected: "#4a4f58",
  unfocused: "#4a4f58",
  textDark: "#e0e2e6",
  textSecondary: "#9ba1ab",
  axisText: "#d0d2d6",
  growthPositive: "#e8a85c",
  growthNegative: "#6bc5d4",
  tableRowHover: "#2a2f38",
  tooltipHeader: "#333842",
  lightBorder: "#3a3f4a",
  backgroundDark: "#151820",
  backgroundMedium: "#2e333c",
  loadingBackground: "#555d6a",
  primaryAction: "#5d8ab5",
  primaryActionHover: "#3a5570",
  accentBlue: "#7b92d0",
};

// ─── Layout tokens (non-themed) ─────────────────────────────────────────────

/** Chart margin configuration. */
export interface ChartMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Layout sizing tokens for data visualization components. */
export interface DataVizLayoutTokens {
  tooltipWidth: number;
  tooltipOffset: number;
  tooltipCloseInset: number;
  tooltipIconSize: number;
  tooltipTriggerSize: string;
  tooltipTriggerMargin: string;
  arrowWidth: number;
  arrowHeight: number;
  noticeInset: number;
  defaultChartMargin: ChartMargins;
}

export const dataVizLayout: DataVizLayoutTokens = {
  tooltipWidth: 250,
  tooltipOffset: 15,
  tooltipCloseInset: 5,
  tooltipIconSize: 36,
  tooltipTriggerSize: "0.7rem",
  tooltipTriggerMargin: "0 0.4rem",
  arrowWidth: 16,
  arrowHeight: 8,
  noticeInset: 30,
  defaultChartMargin: { top: 10, left: 90, right: 50, bottom: 25 },
};

/** Typography tokens for data visualization components. */
export interface DataVizTypographyTokens {
  axisLabel: string;
  totalValueSize: string;
  totalValueWeight: number;
}

export const dataVizTypography: DataVizTypographyTokens = {
  axisLabel: "0.9rem",
  totalValueSize: "clamp(14px, 1.5vw, 18px)",
  totalValueWeight: 600,
};

// ─── CSS custom property references ──────────────────────────────────────────
// Consume these in components — values are injected at runtime by ThemeVars.

export const dataVizColors = {
  neutral: "var(--dv-neutral)",
  strokeGray: "var(--dv-strokeGray)",
  strokeLightGray: "var(--dv-strokeLightGray)",
  gridline: "var(--dv-gridline)",
  overlay: "var(--dv-overlay)",
  overlayLight: "var(--dv-overlayLight)",
  arrowPositive: "var(--dv-arrowPositive)",
  arrowNegative: "var(--dv-arrowNegative)",
  sortArrowUnselected: "var(--dv-sortArrowUnselected)",
  unfocused: "var(--dv-unfocused)",
  textDark: "var(--dv-textDark)",
  textSecondary: "var(--dv-textSecondary)",
  axisText: "var(--dv-axisText)",
  growthPositive: "var(--dv-growthPositive)",
  growthNegative: "var(--dv-growthNegative)",
  tableRowHover: "var(--dv-tableRowHover)",
  tooltipHeader: "var(--dv-tooltipHeader)",
  lightBorder: "var(--dv-lightBorder)",
  backgroundDark: "var(--dv-backgroundDark)",
  backgroundMedium: "var(--dv-backgroundMedium)",
  loadingBackground: "var(--dv-loadingBackground)",
  primaryAction: "var(--dv-primaryAction)",
  primaryActionHover: "var(--dv-primaryActionHover)",
  accentBlue: "var(--dv-accentBlue)",
} as const;
